/* FileInstanceEditor.js */

"use strict";
const GameEditor = require("@gluenetworks/game_editor");
const _ = require("lodash");

// const { VENDOR_LIST_FILTER, IMAGE_TYPES_FILTER } = require("../../lmrbcomponents/fileexplorer/fileConstants");

class FileInstanceEditor extends GameEditor {
    constructor(
        logger,
        di,
        editorState,
        editorLayoutMgr,
        OsImageEntity,
        userSession,
        editorTabCmpnt,
        solutionsManager,
        osImagePathsEntity,
        osUpgradeInfoEntity
    ) {
        super(logger, di, editorState.editorState, editorLayoutMgr);
        this._logger = logger;
        this._osImageEntity = OsImageEntity;
        this._tabCmpnt = editorTabCmpnt;
        this._editorTabState = editorState;
        this._activeOrgId = userSession.activeOrganization.id;
        this._osImagePathsEntity = osImagePathsEntity;
        this._osUpgradeInfoEntity = osUpgradeInfoEntity;
        this._osImagePaths = undefined;
        this._imageTypeFilter = {};
        this._vendorListFilter = {};

        let solutionDefinition = solutionsManager.getActiveSolution();
        let fileEditorViews = _.get(solutionDefinition, "fileEditor.viewNamespaces", []);
        let fileEditorActions = _.get(solutionDefinition, "fileEditor.actionNamespaces", []);
        let launchOptions = editorState.launchOptions;
        this.setNamespaceGlobs(fileEditorViews.concat(fileEditorActions), true);

        this.currentEntityRemoved = false;
        this.currentEntityDeleteInProgress = false;

        this._instanceEditorState = {};
        this._osImage = undefined;
        this._osImageId = launchOptions.imageId;
        this._osImageIds = launchOptions.imageIds;
        this.retardActions(true);
        this._isMultipleEdit = this._osImageIds.length > 1;
        this.whenEditorIsReady = _.memoize(this.whenEditorIsReady);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListenersOnFile() {
        if (this._osImage) {
            this._osImage.on(
                "NamePropertyUpdated",
                (this._onNamePropertyUpdated = this._onNamePropertyUpdated.bind(this))
            );
            this._osImage.on(
                "EntityDeletedFromSystem",
                (this._onFileRemoved = this._onFileRemoved.bind(this))
            );
            this._osImage.on(
                "DirtyStateChanged",
                (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this))
            );
        }
        this._osImagePaths.on(
            "PristinePropertiesChanged",
            (this._onImagePathsPristinePropertiesChanged =
                this._onImagePathsPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterListenersOnFile() {
        if (this._osImage) {
            this._osImage.removeListener("NamePropertyUpdated", this._onNamePropertyUpdated);
            this._osImage.removeListener("EntityDeletedFromSystem", this._onFileRemoved);
            this._osImage.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        }
        this._osImagePaths.removeListener(
            "PristinePropertiesChanged",
            this._onImagePathsPristinePropertiesChanged
        );
    }

    _onDirtyStateChanged(newVal) {
        this._tabCmpnt.setDirty(newVal);
    }

    _onNamePropertyUpdated() {
        // Removing UUID prefix

        const name = this._osImage.getName();
        this.updateTab(name);
    }

    _onFileRemoved() {
        this.currentEntityRemoved = true;
        if (!this.currentEntityDeleteInProgress) {
            this.currentEntityRemoved = false;
            this.closeEditor(this.editorTabStateId, true);
        }
    }

    _onImagePathsPristinePropertiesChanged(/*props, before*/) {
        this._osImage.emit("ImagePathsPristinePropertiesChanged");
    }

    getTabCmpnt() {
        return this._tabCmpnt;
    }

    getOsImage() {
        return this._osImage;
    }

    getOsImages() {
        return this._osImages;
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get osImageEntity() {
        return this._osImageEntity;
    }

    get editorTabStateId() {
        return this._editorTabState.id;
    }

    get osImagePaths() {
        return this._osImagePaths;
    }

    whenEditorIsReady() {
        const osImagePromise = this._isMultipleEdit ? this.loadOsImages() : this.retrieveOsImage();
        return Promise.all([
            osImagePromise,
            this._osImagePathsEntity.retrievePaths(this.activeOrgId),
            this._osUpgradeInfoEntity.loadOsUpgradeInfo(this.activeOrgId, {
                imageTypeFilter: true,
                vendorListFilter: true,
            }),
        ]).then(([osImage, osImagePaths, osUpgradeInfo]) => {
            this._osImagePaths = osImagePaths;

            if (osUpgradeInfo) {
                this._imageTypeFilter = osUpgradeInfo.getImageTypeFilter() || {};
                this._vendorListFilter = osUpgradeInfo.getVendorListFilter() || {};
            }

            this._registerListenersOnFile();
            return osImage;
        });
    }

    retrieveOsImage() {
        return this._osImageEntity
            .retrieveAscendOsImage(this.activeOrgId, this._osImageId)
            .then((osImage) => {
                this._osImage = osImage;
                return osImage;
            });
    }

    loadOsImages() {
        return this._osImageEntity
            .loadAscendOsImages(this.activeOrgId, {id: {$in: this._osImageIds}})
            .then((osImages) => {
                this._osImages = osImages;
                return osImages;
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    updateTab(name) {
        this._tabCmpnt.setTitle(name);
    }

    updateTabIcon(iconName) {
        this._tabCmpnt.setEditorIconName(iconName);
    }

    closeEditor(id, force) {
        id = id || this._editorTabState.id;
        return super.closeEditor(id, force);
    }

    shutdown() {
        let logger = this._logger;
        this.whenEditorIsReady.cache.clear();
        this._unregisterListenersOnFile();

        return Promise.all([
            _.invoke(this._osImage, "shutdown"),
            _.invoke(this._osImagePaths, "shutdown"),
        ])
            .then(() => super.shutdown())
            .then(() => {
                this._logger = undefined;
                this._editorTabState = undefined;
                this._tabCmpnt = undefined;
                this._activeOrgId = undefined;
                this._osImage = undefined;
                this._osImageId = undefined;
                this._osImageIds = undefined;
                this._osImageEntity = undefined;
                this._osImagePathsEntity = undefined;
                this._osImagePaths = undefined;
                this._osUpgradeInfoEntity = undefined;
                this._imageTypeFilter = undefined;
                this._vendorListFilter = undefined;
                this._instanceEditorState = undefined;
                this._isMultipleEdit = undefined;
                this.currentEntityRemoved = undefined;
                this.currentEntityDeleteInProgress = undefined;
            })
            .catch((err) => {
                logger.warn({error: err}, "FileInstanceEditor.shutdown() error");
            });
    }

    setInstanceEditorStateProperty(name, val) {
        _.set(this._instanceEditorState, name, val);
    }

    getInstanceEditorStateProperty(name) {
        return _.get(this._instanceEditorState, name);
    }

    /*******************************************/
    /* END PUBLIC COMPONENT MODEL METHODS      */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getOsImageJType() {
        const readOnlyFieldIds = [
            "createdOn",
            "createdBy",
            "updatedBy",
            "updatedOn",
            "filePath",
            "fileSize",
            "fileUid",
            "checksums",
        ];
        const {vendorValues, vendorLabels} = Object.values(this._vendorListFilter).reduce(
            ({vendorValues, vendorLabels}, {name, title}) => ({
                vendorValues: vendorValues.concat(name),
                vendorLabels: vendorLabels.concat(title),
            }),
            {vendorValues: [], vendorLabels: []}
        );

        const {imageTypeValues, imageTypeLabels} = Object.values(this._imageTypeFilter).reduce(
            ({imageTypeValues, imageTypeLabels}, {name, title}) => ({
                imageTypeValues: imageTypeValues.concat(name),
                imageTypeLabels: imageTypeLabels.concat(title),
            }),
            {imageTypeValues: [], imageTypeLabels: []}
        );

        let jtype = {};
        jtype.name = "";
        jtype["name@Display.label"] = "Name";

        jtype.vendor = "";
        jtype["vendor@Display.label"] = "Vendor";
        jtype["vendor@Enum"] = vendorValues;
        jtype["vendor@Display.enum"] = vendorLabels;

        jtype.imageType = "";
        jtype["imageType@Display.label"] = "Image Type";
        jtype["imageType@Enum"] = imageTypeValues;
        jtype["imageType@Display.enum"] = imageTypeLabels;

        jtype.fileType = "";
        jtype["fileType@Display.hidden"] = true;

        jtype.fileSize = "";
        jtype["fileSize@Display.label"] = "File Size";

        jtype.checksum = "";
        jtype["checksum@Display.label"] = "Checksum";

        jtype.filePath = "";
        jtype["filePath@Display.label"] = "Path";

        jtype.fileUid = "";
        jtype["fileUid@Display.label"] = "ID";

        jtype.createdOn = "";
        jtype["createdOn@Display.label"] = "Created On";

        jtype.createdBy = "";
        jtype["createdBy@Display.label"] = "Created By";

        jtype.updatedOn = "";
        jtype["updatedOn@Display.label"] = "Updated On";

        jtype.updatedBy = "";
        jtype["updatedBy@Display.label"] = "Updated By";

        //update jtype according to readOnly fields list
        readOnlyFieldIds.forEach(
            (fieldId) =>
                _.has(jtype, [fieldId]) && _.set(jtype, [`${fieldId}@Display.readOnly`], true)
        );

        return jtype;
    }

    getOsImagePristineValues(osImage) {
        let pristineProperties = osImage.getPristineProperties();
        let pristineValue = {};

        // Removing UUID prefix
        let name = _.get(pristineProperties, "name", "");
        if (name[36] === "/") {
            name = name.substring(37);
        }

        let fileSize = pristineProperties.fileSize;
        if (isNaN(fileSize)) {
            fileSize = "";
        } else {
            fileSize = fileSize.toLocaleString() + " bytes";
        }

        if (osImage) {
            pristineValue.name = name;
            pristineValue.vendor = pristineProperties.vendor;
            pristineValue.fileType = pristineProperties.fileType;
            pristineValue.imageType = pristineProperties.imageType;
            pristineValue.fileSize = fileSize;
            pristineValue.checksum = _.get(pristineProperties, ["checksums", "md5"], null);
            pristineValue.filePath = this._osImagePaths.getAbsolutePath(pristineProperties.pathId);
            pristineValue.fileUid = pristineProperties.fileUid;
            pristineValue.createdOn = dateString(osImage.createdOn);
            pristineValue.createdBy = osImage.createdBy;
            pristineValue.updatedBy = osImage.updatedBy;
            pristineValue.updatedOn = dateString(osImage.updatedOn);
        }

        return pristineValue;

        function dateString(timeStamp) {
            const date = new Date(timeStamp);
            return date.isValid()
                ? date.toLocaleDateString() + " " + date.toLocaleTimeString()
                : "";
        }
    }
    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE API METHODS                 */
    /*******************************************/
}

module.exports = FileInstanceEditor;

FileInstanceEditor.$inject = [
    "$teve.logger",
    "$teve.di",
    "editorLayout.editorState",
    "editorLayout.editorLayoutMgr",
    "$teve.entities.OsImage",
    "$teve.userSession",
    "editorLayout.editor.tabComponent",
    "$teve.solutionsManager",
    "$teve.entities.OsImagePaths",
    "$teve.entities.OsUpgradeInfo",
];

//Optional convenient way to set a tab icon.
FileInstanceEditor.$editorIcon = "fileserver.file";
FileInstanceEditor.$editorIconType = "base-icon-gl";
